import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useAccount } from "@/queries/account";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/contexts/AuthContext";
import {
  ArrowLeft,
  Bell,
  CheckCircle2,
  ChevronRight,
  ClipboardList,
  CircleAlert,
  Eye,
  FileText,
  Info,
  Loader2,
  LogOut,
  Megaphone,
  Palette,
  Shield,
  Sparkles,
  User,
} from "lucide-react";
import { formatSvDateTime } from "@/utils/date";
import ThemeToggle from "@/components/ThemeToggle";
import { useAccountAttention } from "@/hooks/useAccountAttention";
import { getNativePlatform } from "@/lib/runtime";
import NativeAccountLayout from "@/components/native/NativeAccountLayout";

const ACCOUNT_SCROLL_KEY = "accountPage.scrollY";

export default function AccountPage() {
  const { data: account, isLoading, isError } = useAccount();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { needsNotificationsAttention, needsProtocolAttention } = useAccountAttention();

  const [loggingOut, setLoggingOut] = useState(false);
  const logoutDelayRef = useRef(null);
  const restoredRootScrollRef = useRef(false);
  const onAccountRoot = pathname === "/account";
  const hideAccountOverview = pathname.startsWith("/account/matches/saved/");

  useEffect(() => {
    return () => {
      if (logoutDelayRef.current) {
        clearTimeout(logoutDelayRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!onAccountRoot) {
      restoredRootScrollRef.current = false;
      return;
    }

    const savedScroll = Number(sessionStorage.getItem(ACCOUNT_SCROLL_KEY) || 0);
    if (!restoredRootScrollRef.current) {
      restoredRootScrollRef.current = true;
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: savedScroll, behavior: "auto" });
      });
    }

    const handleScroll = () => {
      sessionStorage.setItem(ACCOUNT_SCROLL_KEY, String(window.scrollY));
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [onAccountRoot]);

  const navItems = [
    { to: "matches", label: "Mina matchprotokoll", icon: ClipboardList, description: "Skapade och sparade protokoll" },
    { to: "profile", label: "Profil", icon: User, description: "Visa och redigera din profil" },
    { to: "appearance", label: "Tema & visning", icon: Palette, description: "Tema, layout och protokollinställningar" },
    { to: "tutorials", label: "Snabbguider", icon: Sparkles, description: "Återställ intro och sidotutorials" },
    { to: "notifications", label: "Notiser", icon: Bell, description: "Hur och när du får aviseringar" },
    { to: "security", label: "Säkerhet", icon: Shield, description: "Lösenord, 2FA och inloggningar" },
    { to: "announcements", label: "Meddelanden från Lapstr", icon: Megaphone, description: "Aktuell information från Lapstr" },
    { to: "privacy", label: "Data & sekretess", icon: Eye, description: "Integritet och datainställningar" },
    { to: "legal", label: "Villkor & integritet", icon: FileText, description: "Integritetspolicy, användarvillkor och cookies" },
    { to: "about", label: "Om appen", icon: Info, description: "Version, status och credits" },
  ];

  const navSections = [
    {
      title: "Konto",
      items: ["profile", "security", "notifications"],
    },
    {
      title: "App",
      items: ["matches", "appearance", "tutorials"],
    },
    {
      title: "Information",
      items: ["announcements", "privacy", "legal", "about"],
    },
  ];

  const navItemMap = Object.fromEntries(navItems.map((item) => [item.to, item]));
  const overviewQuickLinks = [
    {
      key: "protocols",
      label: typeof account?.stats?.protocols === "number" ? `${account.stats.protocols} protokoll` : "Mina protokoll",
      icon: ClipboardList,
      to: "/account/matches",
      className: "hover:border-primary/40 hover:bg-primary/5",
      clickable: true,
    },
    {
      key: "email",
      label: account?.email_verified ? "E-post verifierad" : "E-post overifierad",
      icon: account?.email_verified ? CheckCircle2 : CircleAlert,
      to: "/account/profile#email",
      className: account?.email_verified
        ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
        : "hover:border-amber-500/40 hover:bg-amber-500/10",
      clickable: !account?.email_verified,
    },
    {
      key: "twofa",
      label: account?.two_factor_enabled ? "2FA aktiv" : "2FA inaktiv",
      icon: Shield,
      to: "/account/security#twofa",
      className: account?.two_factor_enabled
        ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
        : "hover:border-border hover:bg-muted/60",
      clickable: !account?.two_factor_enabled,
    },
  ];

  const initials =
    (account?.display_name || account?.email || "")
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((s) => s[0]?.toUpperCase())
      .join("") || " ";

  const activeSection =
    navItems.find(({ to }) => pathname === `/account/${to}` || pathname.startsWith(`/account/${to}/`)) || null;

  const handleLogoutClick = () => {
    if (loggingOut) return;
    setLoggingOut(true);
    logoutDelayRef.current = setTimeout(() => {
      logoutDelayRef.current = null;
      logout();
      navigate("/auth", { replace: true });
    }, 500);
  };

  if (getNativePlatform() === "android") {
    if (hideAccountOverview) {
      return <Outlet />;
    }

    return (
      <NativeAccountLayout
          account={account}
          activeSection={activeSection}
          initials={initials}
          isError={isError}
          isLoading={isLoading}
          loggingOut={loggingOut}
          navItems={navItems}
          needsNotificationsAttention={needsNotificationsAttention}
          needsProtocolAttention={needsProtocolAttention}
          onAccountRoot={onAccountRoot}
          onLogout={handleLogoutClick}
        >
          <Outlet />
      </NativeAccountLayout>
    );
  }

  return (
    <div className={`mx-auto max-w-5xl ${hideAccountOverview ? "space-y-3" : "space-y-6"}`}>
      {!hideAccountOverview && (
        <Card
          id="accountOverviewCard"
          className="overflow-hidden border-primary/20 bg-gradient-to-br from-card via-card to-primary/10"
        >
          <CardContent className="p-6">
            <div className="flex flex-col gap-5">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex min-w-0 items-center gap-4">
                  {isLoading ? (
                    <div className="h-14 w-14 overflow-hidden rounded-full">
                      <Skeleton className="h-full w-full rounded-full" />
                    </div>
                  ) : (
                    <Avatar className="h-14 w-14">
                      <AvatarImage
                        src={account?.avatar_url || ""}
                        alt={account?.display_name || account?.email || "User"}
                      />
                      <AvatarFallback>{initials}</AvatarFallback>
                    </Avatar>
                  )}

                  <div className="min-w-0">
                    {isLoading ? (
                      <>
                        <Skeleton className="mb-2 h-5 w-44" />
                        <Skeleton className="h-4 w-56" />
                      </>
                    ) : (
                      <>
                        <div className="truncate text-xl font-semibold">
                          {account?.display_name || account?.email || ""}
                        </div>
                        <p className="mt-1 text-sm text-muted-foreground">
                          Hantera inställningar.
                        </p>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex items-start gap-3 sm:items-center">
                  <div className="flex flex-wrap gap-2 sm:justify-end">
                    {isLoading ? (
                      <>
                        <Skeleton className="h-6 w-28 rounded-full" />
                        <Skeleton className="h-6 w-40 rounded-full" />
                        <Skeleton className="h-6 w-28 rounded-full" />
                      </>
                    ) : (
                      <>
                        {overviewQuickLinks.map(({ key, label, icon: Icon, to, className, clickable }) => (
                          <button
                            key={key}
                            type="button"
                            onClick={() => clickable && navigate(to)}
                            disabled={!clickable}
                            className={`inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-secondary px-3 py-1.5 text-xs font-medium text-secondary-foreground transition-colors disabled:cursor-default disabled:opacity-100 ${className}`}
                          >
                            <Icon className="h-3.5 w-3.5" />
                            <span>{label}</span>
                          </button>
                        ))}
                      </>
                    )}
                  </div>
                  <ThemeToggle />
                </div>
              </div>

              <div className="border-t border-border/70 pt-4 text-sm">
                {onAccountRoot ? (
                  <div className="flex min-w-0 items-center justify-center gap-2 text-muted-foreground">
                    {/* <ChevronRight className="h-4 w-4 shrink-0 opacity-70" /> */}
                    <span className="truncate font-medium text-foreground">Mina sidor</span>
                  </div>
                ) : (
                  <Link
                    to="/account"
                    className="group relative flex items-center rounded-lg text-muted-foreground transition-colors hover:text-foreground"
                    aria-label="Gå tillbaka till Mina sidor"
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <ArrowLeft className="h-4 w-4 shrink-0 opacity-70 transition-transform group-hover:-translate-x-0.5" />
                      <span className="truncate">Tillbaka</span>
                    </div>
                    <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 truncate font-medium text-foreground">
                      {activeSection?.label || "Mina sidor"}
                    </span>
                  </Link>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {onAccountRoot && (
        // <div id="accountNavGrid" className="space-y-6">
        <div className="space-y-6">
          {navSections.map(({ title, items }) => (
            <section id="accountNavGrid" key={title} className="space-y-3">
              <div className="space-y-1">
                <h2 className="text-sm font-semibold tracking-wide text-foreground">{title}</h2>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {items.map((key) => {
                  const item = navItemMap[key];
                  if (!item) return null;
                  const { to, label, icon: Icon, description: itemDescription } = item;

                  return (
                    <Link key={to} to={to} className="group">
                      <Card className="rounded-xl hover:border-primary/40 hover:shadow-md">
                        <CardContent className="flex items-start gap-3 p-4">
                          {Icon ? (
                            <div className="mt-1">
                              <Icon className="h-5 w-5 opacity-70" aria-hidden />
                            </div>
                          ) : null}
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 font-medium">
                              <span>{label}</span>
                              {to === "notifications" && needsNotificationsAttention ? (
                                <span className="h-2.5 w-2.5 rounded-full bg-red-500" aria-label="Kräver uppmärksamhet" />
                              ) : null}
                              {to === "matches" && needsProtocolAttention ? (
                                <span className="h-2.5 w-2.5 rounded-full bg-red-500" aria-label="Kräver uppmärksamhet" />
                              ) : null}
                            </div>
                            {itemDescription ? (
                              <p className="truncate text-sm text-muted">
                                {itemDescription}
                              </p>
                            ) : null}
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}

      {onAccountRoot && (
        <div className="flex justify-end pt-2">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                type="button"
                variant="outline"
                className="w-full sm:w-auto"
                disabled={loggingOut}
              >
                {loggingOut ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  <LogOut className="h-4 w-4" aria-hidden />
                )}
                <span>{loggingOut ? "Loggar ut..." : "Logga ut"}</span>
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Logga ut?</AlertDialogTitle>
                <AlertDialogDescription>
                  Du loggas ut från den här enheten och skickas tillbaka till inloggningen.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Avbryt</AlertDialogCancel>
                <AlertDialogAction onClick={handleLogoutClick}>Logga ut</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}

      {isError && (
        <div className="text-sm text-red-600">
          Kunde inte hämta kontouppgifter just nu.
        </div>
      )}

      <Outlet />
    </div>
  );
}
